import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    Stack,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import SeccionArticulos from "./SeccionArticulos";
import SeccionAsignacion from "./SeccionAsignacion";
import InventariosService from "../../api/inventarios.api";
import { useStocks } from "../../hooks/useStocks";
import { esPreparador } from "../../helpers/preparadores";
// DECISION (TASK-009): import directo del módulo API del feature "usuarios",
// NO del barrel ("features/usuarios"). El barrel también exporta la página
// ListaUsuarios, que arrastra StateProvider y crea un ciclo de imports (ver
// DECISION en src/services/usuarios.service.js). api/usuarios.api.js solo
// depende de axios/HostService/AuthService, así que importarlo directo lo
// evita.
import UsuariosService from "../../../usuarios/api/usuarios.api";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";

const DEBOUNCE_MS = 300;
const BUSQUEDA_MIN_CARACTERES = 2;
const DURACION_RESALTADO_MS = 1200;

/**
 * Formulario controlado de alta/edición de una lista de inventario. Recibe
 * los valores iniciales por props (`inventario`, o null en alta) y notifica
 * al padre mediante callbacks (`onGuardar`, `onCancel`); NO llama a
 * InventariosService.crear/actualizar (eso lo decide InventarioFormPage,
 * TASK-010, que es quien conoce si está creando o editando y gestiona el
 * loading real de la petición vía `guardando`). Solo llama directamente a
 * InventariosService.buscarArticulos (para el autocompletado de artículos) y
 * a UsuariosService.list() (para el selector de preparador).
 */
const FormularioInventario = ({ inventario, guardando, onGuardar, onCancel }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const esEdicion = Boolean(inventario?.id);

    const [preparadorId, setPreparadorId] = useState("");
    const [lineas, setLineas] = useState([]);
    const [fechaLimite, setFechaLimite] = useState("");
    const [observaciones, setObservaciones] = useState("");
    const [tocado, setTocado] = useState({});

    // Buscador de artículos (Autocomplete con debounce, mismo patrón que
    // PeticionForm.js: solo busca con >=2 caracteres, limpia con cleanup)
    const [q, setQ] = useState("");
    const [opciones, setOpciones] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [lineaResaltada, setLineaResaltada] = useState(null);

    const { stocks: stocksEdicion, ubicaciones: ubicacionesEdicion } = useStocks(inventario?.lineas, esEdicion);

    // Preparadores: UsuariosService.list() (ver DECISION de cabecera)
    const [usuarios, setUsuarios] = useState([]);
    const [cargandoUsuarios, setCargandoUsuarios] = useState(true);

    // Precarga en edición; en alta llega `inventario` como null y limpia
    useEffect(() => {
        setPreparadorId(inventario?.preparadorId ?? "");
        setLineas(
            (inventario?.lineas ?? []).map((linea) => ({
                referencia: linea.referencia,
                nombre: linea.nombre,
                ean: linea.ean,
            }))
        );
        setFechaLimite(inventario?.fechaLimite ?? "");
        setObservaciones(inventario?.observaciones ?? "");
        setTocado({});
    }, [inventario]);

    useEffect(() => {
        let vivo = true;

        const buscarArticulos = async () => {
            if (!q || q.trim().length < BUSQUEDA_MIN_CARACTERES) {
                setOpciones([]);
                return;
            }
            setBuscando(true);
            try {
                const resultados = await InventariosService.buscarArticulos(q.trim());
                if (vivo) setOpciones(resultados ?? []);
            } catch (error) {
                console.error("Error al buscar artículos:", error);
                if (vivo) setOpciones([]);
            } finally {
                if (vivo) setBuscando(false);
            }
        };

        const temporizador = setTimeout(buscarArticulos, DEBOUNCE_MS);
        return () => {
            vivo = false;
            clearTimeout(temporizador);
        };
    }, [q]);

    useEffect(() => {
        let vivo = true;
        UsuariosService.list()
            .then((data) => { if (vivo) setUsuarios(data); })
            .catch((error) => console.error("Error al cargar preparadores:", error))
            .finally(() => { if (vivo) setCargandoUsuarios(false); });
        return () => { vivo = false; };
    }, []);

    // Añadir un artículo ya presente NO duplica la línea: se ignora la
    // segunda adición y se resalta brevemente la existente (TASK-009)
    const handleAgregarLinea = (articulo) => {
        if (!articulo) return;

        const yaExiste = lineas.some((linea) => linea.referencia === articulo.referencia);
        if (yaExiste) {
            setLineaResaltada(articulo.referencia);
            setTimeout(() => setLineaResaltada(null), DURACION_RESALTADO_MS);
        } else {
            setLineas((prev) => [
                ...prev,
                {
                    referencia: articulo.referencia,
                    nombre: articulo.nombre,
                    ean: articulo.ean,
                    stockPorAlmacen: articulo.stockPorAlmacen,
                    ubicacion: articulo.ubicacion,
                },
            ]);
            setTocado((prev) => ({ ...prev, lineas: true }));
        }
        setQ("");
        setOpciones([]);
    };

    const handleQuitarLinea = (referencia) => {
        setLineas((prev) => prev.filter((linea) => linea.referencia !== referencia));
    };

    // Las líneas recién buscadas traen su stock y ubicación; las de un inventario en edición, no
    const obtenerStock = (linea) => linea.stockPorAlmacen ?? stocksEdicion[linea.referencia];
    const obtenerUbicacion = (linea) => linea.ubicacion ?? ubicacionesEdicion[linea.referencia];

    const usuariosOptions = useMemo(
        () => usuarios.filter(esPreparador).map((usuario) => ({ value: usuario.id, label: usuario.username })),
        [usuarios]
    );

    const marcarTocado = (campo) => setTocado((prev) => ({ ...prev, [campo]: true }));

    const errores = {
        // DECISION: el almacén ya no es obligatorio al crear. El preparador elige
        // en qué nave(s) cuenta durante el proceso, según donde encuentre stock
        lineas: lineas.length > 0 ? "" : "Añade al menos un artículo",
        preparador: preparadorId ? "" : "Selecciona un preparador",
    };
    const formularioValido = Object.values(errores).every((mensaje) => !mensaje);

    // El aviso de "líneas" sale en cuanto se toca cualquier campo, no solo el de
    // líneas: `tocado.lineas` solo se marca al AÑADIR una, cuando ya no hay error
    const algunCampoTocado = Boolean(tocado.lineas || tocado.preparador);

    const handleChangePreparador = (event) => {
        const idSeleccionado = event.target.value;
        setPreparadorId(idSeleccionado);
        marcarTocado("preparador");
    };

    const handleGuardarClick = () => {
        if (!formularioValido) {
            setTocado({ lineas: true, preparador: true });
            return;
        }

        onGuardar({
            // DECISION: ni almacén ni stockTeorico: el preparador elige la nave al
            // contar y el backend congela el stock de esa nave en ese momento
            preparadorId,
            lineas: lineas.map((linea) => ({
                referencia: linea.referencia,
                nombre: linea.nombre,
                ean: linea.ean,
            })),
            fechaLimite: fechaLimite || null,
            observaciones,
        });
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: "#212529", mb: 0.5 }}>
                            {esEdicion ? "Editar lista de inventario" : "Nueva lista de inventario"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Busca artículos por referencia o EAN y asigna un preparador. Al contar, se elige la(s) nave(s)
                        </Typography>
                    </Box>

                    <Stack spacing={3}>
                        {/* Artículos - se ven stocks de todas las naves */}
                        <SeccionArticulos
                            opciones={opciones}
                            buscando={buscando}
                            q={q}
                            onInputChangeQ={setQ}
                            onAgregarLinea={handleAgregarLinea}
                            mostrarErrorLineas={algunCampoTocado}
                            errorLineas={errores.lineas}
                            lineas={lineas}
                            isMobile={isMobile}
                            lineaResaltada={lineaResaltada}
                            obtenerStock={obtenerStock}
                            obtenerUbicacion={obtenerUbicacion}
                            onQuitarLinea={handleQuitarLinea}
                        />

                        <SeccionAsignacion
                            preparadorId={preparadorId}
                            usuariosOptions={usuariosOptions}
                            cargandoUsuarios={cargandoUsuarios}
                            onChangePreparador={handleChangePreparador}
                            onBlurPreparador={() => marcarTocado("preparador")}
                            mostrarErrorPreparador={tocado.preparador}
                            errorPreparador={errores.preparador}
                            fechaLimite={fechaLimite}
                            onChangeFechaLimite={(event) => setFechaLimite(event.target.value)}
                            observaciones={observaciones}
                            onChangeObservaciones={(event) => setObservaciones(event.target.value)}
                        />

                        {algunCampoTocado && !formularioValido && (
                            <Alert severity="warning">Añade artículos y asigna un preparador antes de guardar.</Alert>
                        )}

                        {/* Botones */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={guardando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
                                onClick={handleGuardarClick}
                                disabled={guardando || !formularioValido}
                                fullWidth={isMobile}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                {guardando ? "Guardando..." : "Guardar lista"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={onCancel}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_SECUNDARIO_SX}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default FormularioInventario;
